import { yupResolver } from '@hookform/resolvers/yup'
import { find } from 'lodash-es'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import * as yup from 'yup'

import { SettingsColors } from '../../../common/models'
import FormGroup from '../../../core/components/FormGroup'
import { useCheckPermission } from '../../../core/hooks/use-check-permission'
import {
  getSettingsColors,
  updateSettingsColors,
} from '../../../core/services/festival-settings'
import { useAppSelector } from '../../../store'
import { MessageBox } from '../messages/MessageBox'
import { ColorField } from './settings-components/ColorField'
import { GradientField } from './settings-components/GradientField'
import { Header, PageTitle } from '@/components'
import { Button, Card } from '@/components/ui'

interface ITicketColorValues {
  from: string
  to: string
  fallBackColor: string
}

interface IMainColorsValues {
  mainColor1: string
  mainColor2: string
  mainColor3: string
  mainColor4: string
  mainColor5: string
  mainColor6: ITicketColorValues
}

interface ISecondaryColorsValues {
  gradiant1: ITicketColorValues
  gradiant2: ITicketColorValues
  gradiant3: ITicketColorValues
  gradiant4: ITicketColorValues
  gradiant5: ITicketColorValues
  gradiant6: ITicketColorValues
  gradiant7: ITicketColorValues
}

interface ITertiaryColorsValues {
  gradiantFade: ITicketColorValues
  gradiantMenu: ITicketColorValues
}

export const CustomThemePage: React.FC = () => {
  const navigate = useNavigate()
  const { editionId, festivalName } = useParams()
  const { festivals: storeFestivals } = useAppSelector((s) => s.app)
  const festivalFullName = storeFestivals.find(
    (f) => f.platformName === festivalName,
  )?.name

  const [settingsColor, setSettingsColor] = useState<SettingsColors[]>()

  const [isError, setIsError] = useState<boolean>(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')

  const { hasEditPermission } = useCheckPermission({ page: 'SETTINGS' })

  useEffect(() => {
    // prettier-ignore
    (async () => {
      try {
        const response = await getSettingsColors()
        setSettingsColor(response.data)
        const ticket = find(response.data, data => data.name === 'TICKET')
        const main = find(response.data, data => data.name === 'PRIMARY')
        const secondary = find(response.data, data => data.name === 'SECONDARY')
        const ternary = find(response.data, data => data.name === 'TERTIARY')

        setTimeout(() => {
          if (ticket?.gradients) {
            setTicketColorValue(`from`, ticket.gradients?.[0]?.colors?.[0].color?.value.substring(0, 7))
            setTicketColorValue(`to`, ticket.gradients?.[0]?.colors?.[1].color?.value.substring(0, 7))
            setTicketColorValue(`fallBackColor`, ticket.gradients?.[0]?.fallColor.value.substring(0, 7))
          }
          if (main?.gradients && main?.colors) {
            setMainColorValues(`mainColor1`, main.colors?.[0]?.value.substring(0, 7))
            setMainColorValues(`mainColor2`, main.colors?.[1]?.value.substring(0, 7))
            setMainColorValues(`mainColor3`, main.colors?.[2]?.value.substring(0, 7))
            setMainColorValues(`mainColor4`, main.colors?.[3]?.value.substring(0, 7))
            setMainColorValues(`mainColor5`, main.colors?.[4]?.value.substring(0, 7))
            setMainColorValues(`mainColor6.from`, main.gradients?.[0]?.colors?.[0]?.color?.value.substring(0, 7))
            setMainColorValues(`mainColor6.to`, main.gradients?.[0]?.colors?.[1]?.color?.value.substring(0, 7))
            setMainColorValues(`mainColor6.fallBackColor`, main.gradients?.[0]?.fallColor?.value.substring(0, 7))
          }
          if (secondary?.gradients) {
            setSecondaryColorsValue(`gradiant1.from`, secondary.gradients?.[0]?.colors?.[0]?.color?.value.substring(0, 7))
            setSecondaryColorsValue(`gradiant1.to`, secondary.gradients?.[0]?.colors?.[1]?.color?.value.substring(0, 7))
            if (secondary.gradients?.[0]?.fallColor?.value) {
              setSecondaryColorsValue(`gradiant1.fallBackColor`, secondary.gradients?.[0]?.fallColor?.value?.substring(0, 7))
            }
            setSecondaryColorsValue(`gradiant2.from`, secondary.gradients?.[1]?.colors?.[0]?.color?.value.substring(0, 7))
            setSecondaryColorsValue(`gradiant2.to`, secondary.gradients?.[1]?.colors?.[1]?.color?.value.substring(0, 7))
            if (secondary.gradients?.[1]?.fallColor?.value) {
              setSecondaryColorsValue(`gradiant2.fallBackColor`, secondary.gradients?.[1]?.fallColor?.value.substring(0, 7))
            }
            setSecondaryColorsValue(`gradiant3.from`, secondary.gradients?.[2]?.colors?.[0]?.color?.value.substring(0, 7))
            setSecondaryColorsValue(`gradiant3.to`, secondary.gradients?.[2]?.colors?.[1]?.color?.value.substring(0, 7))
            if (secondary.gradients?.[2]?.fallColor?.value) {
              setSecondaryColorsValue(`gradiant3.fallBackColor`, secondary.gradients?.[2]?.fallColor?.value.substring(0, 7))
            }
            setSecondaryColorsValue(`gradiant4.from`, secondary.gradients?.[3]?.colors?.[0]?.color?.value.substring(0, 7))
            setSecondaryColorsValue(`gradiant4.to`, secondary.gradients?.[3]?.colors?.[1]?.color?.value.substring(0, 7))
            if (secondary.gradients?.[3]?.fallColor?.value) {
              setSecondaryColorsValue(`gradiant4.fallBackColor`, secondary.gradients?.[3]?.fallColor?.value.substring(0, 7))
            }
            setSecondaryColorsValue(`gradiant5.from`, secondary.gradients?.[4]?.colors?.[0]?.color?.value.substring(0, 7))
            setSecondaryColorsValue(`gradiant5.to`, secondary.gradients?.[4]?.colors?.[1]?.color?.value.substring(0, 7))
            if (secondary.gradients?.[4]?.fallColor?.value) {
              setSecondaryColorsValue(`gradiant5.fallBackColor`, secondary.gradients?.[4]?.fallColor?.value.substring(0, 7))
            }
            setSecondaryColorsValue(`gradiant6.from`, secondary.gradients?.[5]?.colors?.[0]?.color?.value.substring(0, 7))
            setSecondaryColorsValue(`gradiant6.to`, secondary.gradients?.[5]?.colors?.[1]?.color?.value.substring(0, 7))
            if (secondary.gradients?.[5]?.fallColor?.value) {
              setSecondaryColorsValue(`gradiant6.fallBackColor`, secondary.gradients?.[5]?.fallColor?.value.substring(0, 7))
            }
            setSecondaryColorsValue(`gradiant7.from`, secondary.gradients?.[6]?.colors?.[0]?.color?.value.substring(0, 7))
            setSecondaryColorsValue(`gradiant7.to`, secondary.gradients?.[6]?.colors?.[1]?.color?.value.substring(0, 7))
            if (secondary.gradients?.[6]?.fallColor?.value) {
              setSecondaryColorsValue(`gradiant7.fallBackColor`, secondary.gradients?.[6]?.fallColor?.value.substring(0, 7))
            }
          }
          if (ternary?.gradients) {
            setTertiaryColorsValue(`gradiantFade.from`, ternary.gradients?.[0]?.colors?.[0]?.color?.value.substring(0, 7))
            setTertiaryColorsValue(`gradiantFade.to`, ternary.gradients?.[0]?.colors?.[1]?.color?.value.substring(0, 7))
            if (ternary.gradients?.[0]?.fallColor?.value) {
              setTertiaryColorsValue(`gradiantFade.fallBackColor`, ternary.gradients?.[0]?.fallColor?.value.substring(0, 7))
            }
            setTertiaryColorsValue(`gradiantMenu.from`, ternary.gradients?.[1]?.colors?.[0]?.color?.value.substring(0, 7))
            setTertiaryColorsValue(`gradiantMenu.to`, ternary.gradients?.[1]?.colors?.[1]?.color?.value.substring(0, 7))
            if (ternary.gradients?.[1]?.fallColor?.value) {
              setTertiaryColorsValue(`gradiantMenu.fallBackColor`, ternary.gradients?.[1]?.fallColor?.value.substring(0, 7))
            }
          }
        }, 0)
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus((e as { response?: { status?: number } })?.response?.status)
        } else {
          console.error(e)
        }
      }
    })()
  }, [editionId, festivalName])

  const gradiantShape = {
    from: yup.string().required('Field is required'),
    to: yup.string().required('Field is required'),
    fallBackColor: yup.string().required('Field is required'),
  }

  const ticketColorsSchema = yup.object().shape(gradiantShape)

  const {
    handleSubmit: handleSubmitTicketColors,
    formState: { errors: ticketColorsErrors },
    control: controlTicketColors,
    watch,
    setValue: setTicketColorValue,
  } = useForm<ITicketColorValues>({
    mode: 'onChange',
    resolver: yupResolver<ITicketColorValues>(ticketColorsSchema),
  })

  const from = watch('from')
  const to = watch('to')

  const submitTicketColors = async (values: ITicketColorValues) => {
    const arr: SettingsColors[] = []
    const ticket = find(settingsColor, (data) => data.name === 'TICKET')
    if (ticket?.gradients) {
      ticket.gradients[0].fallColor.value = values.fallBackColor
      ticket.gradients[0].colors[0].color.value = values.from
      ticket.gradients[0].colors[1].color.value = values.to
    }
    if (ticket) arr.push(ticket)
    const main = find(settingsColor, (data) => data.name === 'PRIMARY')
    if (main) arr.push(main)
    const secondary = find(settingsColor, (data) => data.name === 'SECONDARY')
    if (secondary) arr.push(secondary)
    const ternary = find(settingsColor, (data) => data.name === 'TERTIARY')
    if (ternary) arr.push(ternary)
    try {
      const { status } = await updateSettingsColors(arr)
      if (status === 200) {
        setIsError(false)
        setShowMessageBox(true)
        setMessage('Update Successful')
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    }
  }

  const mainColorsSchema = yup.object().shape({
    mainColor1: yup.string().required('Field is required'),
    mainColor2: yup.string().required('Field is required'),
    mainColor3: yup.string().required('Field is required'),
    mainColor4: yup.string().required('Field is required'),
    mainColor5: yup.string().required('Field is required'),
    mainColor6: yup.object().shape(gradiantShape),
  })

  const {
    handleSubmit: handleSubmitMainColors,
    formState: { errors: mainColorsErrors },
    control: controlMainColors,
    watch: watchMainColors,
    setValue: setMainColorValues,
  } = useForm<IMainColorsValues>({
    mode: 'onChange',
    resolver: yupResolver<IMainColorsValues>(mainColorsSchema),
  })

  const mainColor6 = watchMainColors('mainColor6')

  const submitMainColors = async (values: IMainColorsValues) => {
    const arr: SettingsColors[] = []
    const main = find(settingsColor, (data) => data.name === 'PRIMARY')
    if (main?.colors && main.gradients) {
      main.colors[0].value = values.mainColor1
      main.colors[1].value = values.mainColor2
      main.colors[2].value = values.mainColor3
      main.colors[3].value = values.mainColor4
      main.colors[4].value = values.mainColor5
      main.gradients[0].fallColor.value = values.mainColor6.fallBackColor
      main.gradients[0].colors[0].color.value = values.mainColor6.from
      main.gradients[0].colors[1].color.value = values.mainColor6.to
    }
    const ticket = find(settingsColor, (data) => data.name === 'TICKET')
    if (ticket) arr.push(ticket)
    if (main) arr.push(main)
    const secondary = find(settingsColor, (data) => data.name === 'SECONDARY')
    if (secondary) arr.push(secondary)
    const ternary = find(settingsColor, (data) => data.name === 'TERTIARY')
    if (ternary) arr.push(ternary)
    try {
      const { status } = await updateSettingsColors(arr)
      if (status === 200) {
        setIsError(false)
        setShowMessageBox(true)
        setMessage('Update Successful')
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    }
  }

  const secondaryColorsSchema = yup.object().shape({
    gradiant1: yup.object().shape(gradiantShape),
    gradiant2: yup.object().shape(gradiantShape),
    gradiant3: yup.object().shape(gradiantShape),
    gradiant4: yup.object().shape(gradiantShape),
    gradiant5: yup.object().shape(gradiantShape),
    gradiant6: yup.object().shape(gradiantShape),
    gradiant7: yup.object().shape(gradiantShape),
  })

  const {
    handleSubmit: handleSecondaryColorsSubmit,
    formState: { errors: secondaryColorsErrors },
    control: controlSecondaryColors,
    watch: watchSecondaryColors,
    setValue: setSecondaryColorsValue,
  } = useForm<ISecondaryColorsValues>({
    mode: 'onChange',
    resolver: yupResolver<ISecondaryColorsValues>(secondaryColorsSchema),
  })

  const gradiant1 = watchSecondaryColors('gradiant1')
  const gradiant2 = watchSecondaryColors('gradiant2')
  const gradiant3 = watchSecondaryColors('gradiant3')
  const gradiant4 = watchSecondaryColors('gradiant4')
  const gradiant5 = watchSecondaryColors('gradiant5')
  const gradiant6 = watchSecondaryColors('gradiant6')
  const gradiant7 = watchSecondaryColors('gradiant7')

  const submitSecondaryColors = async (values: ISecondaryColorsValues) => {
    {
      const arr: SettingsColors[] = []
      const secondary = find(settingsColor, (data) => data.name === 'SECONDARY')
      if (secondary?.gradients) {
        secondary.gradients[0].fallColor.value = values.gradiant1.fallBackColor
        secondary.gradients[0].colors[0].color.value = values.gradiant1.from
        secondary.gradients[0].colors[1].color.value = values.gradiant1.to

        secondary.gradients[1].fallColor.value = values.gradiant2.fallBackColor
        secondary.gradients[1].colors[0].color.value = values.gradiant2.from
        secondary.gradients[1].colors[1].color.value = values.gradiant2.to

        secondary.gradients[2].fallColor.value = values.gradiant3.fallBackColor
        secondary.gradients[2].colors[0].color.value = values.gradiant3.from
        secondary.gradients[2].colors[1].color.value = values.gradiant3.to

        secondary.gradients[3].fallColor.value = values.gradiant4.fallBackColor
        secondary.gradients[3].colors[0].color.value = values.gradiant4.from
        secondary.gradients[3].colors[1].color.value = values.gradiant4.to

        secondary.gradients[4].fallColor.value = values.gradiant5.fallBackColor
        secondary.gradients[4].colors[0].color.value = values.gradiant5.from
        secondary.gradients[4].colors[1].color.value = values.gradiant5.to

        secondary.gradients[5].fallColor.value = values.gradiant6.fallBackColor
        secondary.gradients[5].colors[0].color.value = values.gradiant6.from
        secondary.gradients[5].colors[1].color.value = values.gradiant6.to

        secondary.gradients[6].fallColor.value = values.gradiant7.fallBackColor
        secondary.gradients[6].colors[0].color.value = values.gradiant7.from
        secondary.gradients[6].colors[1].color.value = values.gradiant7.to
      }
      const ticket = find(settingsColor, (data) => data.name === 'TICKET')
      if (ticket) arr.push(ticket)
      const main = find(settingsColor, (data) => data.name === 'PRIMARY')
      if (main) arr.push(main)
      if (secondary) arr.push(secondary)
      const ternary = find(settingsColor, (data) => data.name === 'TERTIARY')
      if (ternary) arr.push(ternary)
      try {
        const { status } = await updateSettingsColors(arr)
        if (status === 200) {
          setIsError(false)
          setShowMessageBox(true)
          setMessage('Update Successful')
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus(
            (e as { response?: { status?: number } })?.response?.status,
          )
        } else {
          console.error(e)
        }
      }
    }
  }

  const tertiaryColorsSchema = yup.object().shape({
    gradiantFade: yup.object().shape(gradiantShape),
    gradiantMenu: yup.object().shape(gradiantShape),
  })

  const {
    handleSubmit: handleTertiaryColorsSubmit,
    formState: { errors: tertiaryColorsErrors },
    control: controlTertiaryColors,
    watch: watchTertiaryColors,
    setValue: setTertiaryColorsValue,
  } = useForm<ITertiaryColorsValues>({
    mode: 'onChange',
    resolver: yupResolver<ITertiaryColorsValues>(tertiaryColorsSchema),
  })

  const gradiantFade = watchTertiaryColors('gradiantFade')
  const gradiantMenu = watchTertiaryColors('gradiantMenu')

  const submitTertiaryColors = async (values: ITertiaryColorsValues) => {
    const arr: SettingsColors[] = []
    const ternary = find(settingsColor, (data) => data.name === 'TERTIARY')
    if (ternary?.gradients) {
      ternary.gradients[0].fallColor.value = values.gradiantFade.fallBackColor
      ternary.gradients[0].colors[0].color.value = values.gradiantFade.from
      ternary.gradients[0].colors[1].color.value = values.gradiantFade.to
      ternary.gradients[1].fallColor.value = values.gradiantMenu.fallBackColor
      ternary.gradients[1].colors[0].color.value = values.gradiantMenu.from
      ternary.gradients[1].colors[1].color.value = values.gradiantMenu.to
    }
    const ticket = find(settingsColor, (data) => data.name === 'TICKET')
    if (ticket) arr.push(ticket)
    const main = find(settingsColor, (data) => data.name === 'PRIMARY')
    if (main) arr.push(main)
    const secondary = find(settingsColor, (data) => data.name === 'SECONDARY')
    if (secondary) arr.push(secondary)
    if (ternary) arr.push(ternary)
    try {
      const { status } = await updateSettingsColors(arr)
      if (status === 200) {
        setIsError(false)
        setShowMessageBox(true)
        setMessage('Update Successful')
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    }
  }

  return (
    <>
      <Header pageTitle="Settings" pageSubtitle="Customize Theme" />
      <div className="max-h-[calc(100vh-118px)] overflow-y-auto space-y-4">
        <Card className="p-7.5 bg-gradient-4 border border-monochrome-120">
          <form
            id="settings-ticket-color-form"
            onSubmit={handleSubmitTicketColors(submitTicketColors)}
          >
            <h3 className="text-title-xs text-monochrome-1 mb-6">
              Default Ticket Color
            </h3>
            <div className="space-y-4 bg-monochrome-3-60 border border-monochrome-120 rounded-2xl p-4">
              <FormGroup label="Gradient">
                <div className="flex w-full items-end gap-2">
                  <div className="bg-monochrome-120 border border-monochrome-120 rounded-2xl h-[44px] w-[44px] shrink-0 items-center justify-center flex">
                    <div
                      className="rounded-full w-[24px] h-[24px] border border-monochrome-120 flex-shrink-0"
                      style={{
                        background: `linear-gradient(${from} , ${to})`,
                      }}
                    />
                  </div>
                  <ColorField
                    control={controlTicketColors}
                    name="from"
                    error={ticketColorsErrors.from?.message}
                    disabled={!hasEditPermission}
                  />
                  <ColorField
                    control={controlTicketColors}
                    name="to"
                    error={ticketColorsErrors.to?.message}
                    disabled={!hasEditPermission}
                  />
                </div>
              </FormGroup>
              <ColorField
                control={controlTicketColors}
                name="fallBackColor"
                label="Fall Color"
                error={ticketColorsErrors.fallBackColor?.message}
                disabled={!hasEditPermission}
              />
            </div>
            <div className="flex justify-end gap-2 pt-4 border-t border-monochrome-120 mt-4 [&>*]:w-[154px]">
              <Button
                onClick={() => navigate(-1)}
                variant="secondary"
                data-dismiss="modal"
                disabled={!hasEditPermission}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                form="settings-ticket-color-form"
                type="submit"
                disabled={!hasEditPermission}
              >
                Save
              </Button>
            </div>
          </form>
        </Card>

        <Card className="p-7.5 bg-gradient-4 border border-monochrome-120">
          <h3 className="text-title-xs text-monochrome-1 mb-6">Main Colors</h3>

          <form
            id="settings-main-colors-form"
            onSubmit={handleSubmitMainColors(submitMainColors)}
          >
            <div className="w-full space-y-4">
              <div className="bg-monochrome-3-60 border border-monochrome-120 rounded-2xl p-4">
                <ColorField
                  control={controlMainColors}
                  name="mainColor1"
                  label="Main Color 1"
                  error={mainColorsErrors.mainColor1?.message}
                  disabled={!hasEditPermission}
                />
              </div>
              <div className="bg-monochrome-3-60 border border-monochrome-120 rounded-2xl p-4">
                <ColorField
                  control={controlMainColors}
                  name="mainColor2"
                  label="Main Color 2"
                  error={mainColorsErrors.mainColor2?.message}
                  disabled={!hasEditPermission}
                />
              </div>
              <div className="bg-monochrome-3-60 border border-monochrome-120 rounded-2xl p-4">
                <ColorField
                  control={controlMainColors}
                  name="mainColor3"
                  label="Main Color 3"
                  error={mainColorsErrors.mainColor3?.message}
                  disabled={!hasEditPermission}
                />
              </div>
              <div className="bg-monochrome-3-60 border border-monochrome-120 rounded-2xl p-4">
                <ColorField
                  control={controlMainColors}
                  name="mainColor4"
                  label="Main Color 4"
                  error={mainColorsErrors.mainColor4?.message}
                  disabled={!hasEditPermission}
                />
              </div>
              <div className="bg-monochrome-3-60 border border-monochrome-120 rounded-2xl p-4">
                <ColorField
                  control={controlMainColors}
                  name="mainColor5"
                  label="Main Color 5"
                  error={mainColorsErrors.mainColor5?.message}
                  disabled={!hasEditPermission}
                />
              </div>
              <div className="bg-monochrome-3-60 border border-monochrome-120 rounded-2xl p-4 space-y-3">
                <GradientField
                  control={controlMainColors}
                  baseName="mainColor6"
                  label="Main Color 6"
                  fromValue={mainColor6?.from}
                  toValue={mainColor6?.to}
                  fromError={mainColorsErrors.mainColor6?.from?.message}
                  toError={mainColorsErrors.mainColor6?.to?.message}
                  fallError={
                    mainColorsErrors.mainColor6?.fallBackColor?.message
                  }
                  disabled={!hasEditPermission}
                />
              </div>
            </div>
          </form>

          <div className="flex justify-end gap-2 pt-4 border-t border-monochrome-120 mt-4 [&>*]:w-[154px]">
            <Button
              onClick={() => navigate(-1)}
              variant="secondary"
              data-dismiss="modal"
              disabled={!hasEditPermission}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              form="settings-main-colors-form"
              type="submit"
              disabled={!hasEditPermission}
            >
              Save
            </Button>
          </div>
        </Card>

        <Card className="p-7.5 bg-gradient-4 border border-monochrome-120">
          <h3 className="text-title-xs text-monochrome-1 mb-6">
            Secondary Colors
          </h3>

          <form
            id="settings-secondary-colors-form"
            onSubmit={handleSecondaryColorsSubmit(submitSecondaryColors)}
          >
            <div className="flex flex-col gap-3">
              {(
                [
                  { key: 'gradiant1', value: gradiant1 },
                  { key: 'gradiant2', value: gradiant2 },
                  { key: 'gradiant3', value: gradiant3 },
                  { key: 'gradiant4', value: gradiant4 },
                  { key: 'gradiant5', value: gradiant5 },
                  { key: 'gradiant6', value: gradiant6 },
                  { key: 'gradiant7', value: gradiant7 },
                ] as const
              ).map(({ key, value }, idx) => (
                <div
                  key={key}
                  className="bg-monochrome-3-60 border border-monochrome-120 rounded-2xl p-4 space-y-3"
                >
                  <GradientField
                    control={controlSecondaryColors}
                    baseName={key}
                    label={`Gradient ${idx + 1}`}
                    fromValue={value?.from}
                    toValue={value?.to}
                    fromError={secondaryColorsErrors[key]?.from?.message}
                    toError={secondaryColorsErrors[key]?.to?.message}
                    fallError={
                      secondaryColorsErrors[key]?.fallBackColor?.message
                    }
                    disabled={!hasEditPermission}
                  />
                </div>
              ))}
            </div>
          </form>

          <div className="flex justify-end gap-2 pt-4 border-t border-monochrome-120 mt-4 [&>*]:w-[154px]">
            <Button
              onClick={() => navigate(-1)}
              variant="secondary"
              data-dismiss="modal"
              disabled={!hasEditPermission}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              form="settings-secondary-colors-form"
              type="submit"
              disabled={!hasEditPermission}
            >
              Save
            </Button>
          </div>
        </Card>

        <Card className="p-7.5 bg-gradient-4 border border-monochrome-120">
          <h3 className="text-title-xs text-monochrome-1 mb-6">
            Tertiary Colors
          </h3>

          <form
            id="settings-tertiary-colors-form"
            onSubmit={handleTertiaryColorsSubmit(submitTertiaryColors)}
          >
            <div className="flex flex-col gap-3">
              <div className="bg-monochrome-3-60 border border-monochrome-120 rounded-2xl p-4 space-y-3">
                <GradientField
                  control={controlTertiaryColors}
                  baseName="gradiantFade"
                  label="Gradient Fade"
                  fromValue={gradiantFade?.from}
                  toValue={gradiantFade?.to}
                  fromError={tertiaryColorsErrors.gradiantFade?.from?.message}
                  toError={tertiaryColorsErrors.gradiantFade?.to?.message}
                  fallError={
                    tertiaryColorsErrors.gradiantFade?.fallBackColor?.message
                  }
                  disabled={!hasEditPermission}
                />
              </div>
              <div className="bg-monochrome-3-60 border border-monochrome-120 rounded-2xl p-4 space-y-3">
                <GradientField
                  control={controlTertiaryColors}
                  baseName="gradiantMenu"
                  label="Gradient Menu"
                  fromValue={gradiantMenu?.from}
                  toValue={gradiantMenu?.to}
                  fromError={tertiaryColorsErrors.gradiantMenu?.from?.message}
                  toError={tertiaryColorsErrors.gradiantMenu?.to?.message}
                  fallError={
                    tertiaryColorsErrors.gradiantMenu?.fallBackColor?.message
                  }
                  disabled={!hasEditPermission}
                />
              </div>
            </div>
          </form>

          <div className="flex justify-end gap-2 pt-4 border-t border-monochrome-120 mt-4 [&>*]:w-[154px]">
            <Button
              onClick={() => navigate(-1)}
              variant="secondary"
              data-dismiss="modal"
              disabled={!hasEditPermission}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              form="settings-tertiary-colors-form"
              type="submit"
              disabled={!hasEditPermission}
            >
              Save
            </Button>
          </div>
        </Card>
      </div>
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
        errorStatus={errorStatus}
      />
    </>
  )
}
